/**
 * threadDeepLink — a URL that names a thread (per-thread notifications,
 * 2026-09-08). `/?thread=<threadId>&ws=<accountId>` — the shape the server
 * push fan-out puts in every notification (functions/src/notifications/
 * pushFanout.ts threadDeepLink) and the shape the service worker hands an
 * open window.
 *
 * TWO ways in, ONE decision:
 *   • BOOT — the page loaded with the params (a notification click with no
 *     prest0 window open). GlobalChatSystem consumes them once the thread
 *     store is ready and the workspace has resolved.
 *   • LIVE — a same-origin window received the URL (the service worker's
 *     OPEN_THREAD message, a fallback tab notification's onclick, a bell row
 *     click). It arrives on the `prest0n:open-thread-url` document event.
 *
 * The decision is the combined view's own containment rule (multi-org plan
 * §10, invariant 1 — ONE workspace at a time): a thread in the ACTIVE
 * workspace opens here; a thread bound to ANOTHER workspace runs the SEALED
 * switch (beginWorkspaceTransition — overlay, server-verified switch,
 * pending-open marker, hard navigation) and NEVER activates in place. The
 * params are stripped from the address bar the moment they are consumed, so
 * a reload can never re-open (or re-switch to) the same thread.
 *
 * Pure decision + injected effects, so the rule is unit-tested without a
 * browser; the defaults (history.replaceState, the marker, the transition)
 * are what GlobalChatSystem wires.
 */
import { savePendingThreadOpen } from './pendingThreadOpen';

export const THREAD_DEEP_LINK_PARAM = 'thread';
export const THREAD_DEEP_LINK_WS_PARAM = 'ws';
/**
 * The installed app's "new thread" shortcut (manifest.json) lands on
 * `/?source=pwa&open=chat`: `open=chat` means "open the chat surface" (on a
 * phone that is the fullscreen takeover); `source=pwa` is inert — stripped,
 * no behaviour. Both ride this consumer so ONE place owns the app's URL
 * intents.
 */
export const OPEN_CHAT_PARAM = 'open';
export const OPEN_CHAT_VALUE = 'chat';
export const PWA_SOURCE_PARAM = 'source';
export const PWA_SOURCE_VALUE = 'pwa';
/** Document event carrying a thread URL to an open window (cancelable — preventDefault = "a chat took it"). */
export const OPEN_THREAD_URL_EVENT = 'prest0n:open-thread-url';

export interface ThreadDeepLink {
  threadId: string;
  /** The thread's workspace (account id); null when the link did not say. */
  ws: string | null;
}

export type ThreadDeepLinkDecision =
  | { kind: 'none' }
  | { kind: 'wait' }
  | { kind: 'open-here'; threadId: string }
  | { kind: 'switch-workspace'; threadId: string; ws: string };

export function parseThreadDeepLink(search: string | URLSearchParams | null | undefined): ThreadDeepLink | null {
  if (!search) return null;
  let params: URLSearchParams;
  try {
    params = typeof search === 'string' ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search) : search;
  } catch {
    return null;
  }
  const threadId = (params.get(THREAD_DEEP_LINK_PARAM) || '').trim();
  if (!threadId) return null;
  const ws = (params.get(THREAD_DEEP_LINK_WS_PARAM) || '').trim() || null;
  return { threadId, ws };
}

function toParams(search: string | URLSearchParams | null | undefined): URLSearchParams | null {
  if (!search) return null;
  try {
    return typeof search === 'string' ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search) : search;
  } catch {
    return null;
  }
}

/** `open=chat` — the manifest shortcut's "open the chat surface" intent. */
export function parseChatOpenIntent(search: string | URLSearchParams | null | undefined): boolean {
  const params = toParams(search);
  return Boolean(params && params.get(OPEN_CHAT_PARAM) === OPEN_CHAT_VALUE);
}

/** True when the query carries anything this consumer owns (thread, ws, open=chat, source=pwa). */
export function hasAppUrlIntent(search: string | URLSearchParams | null | undefined): boolean {
  const params = toParams(search);
  if (!params) return false;
  return Boolean(
    (params.get(THREAD_DEEP_LINK_PARAM) || '').trim()
    || params.get(OPEN_CHAT_PARAM) === OPEN_CHAT_VALUE
    || params.get(PWA_SOURCE_PARAM) === PWA_SOURCE_VALUE,
  );
}

/** Parse a full or relative URL's query (the service worker hands a full href). */
export function parseThreadDeepLinkFromUrl(url: string | null | undefined): ThreadDeepLink | null {
  if (!url) return null;
  try {
    const base = typeof window !== 'undefined' && window.location ? window.location.origin : 'https://prest0.ai';
    return parseThreadDeepLink(new URL(url, base).search);
  } catch {
    return null;
  }
}

export function buildThreadDeepLinkUrl(link: { threadId: string; ws?: string | null }, path: string = '/'): string {
  const params = new URLSearchParams();
  params.set(THREAD_DEEP_LINK_PARAM, link.threadId);
  if (link.ws) params.set(THREAD_DEEP_LINK_WS_PARAM, link.ws);
  return `${path}?${params.toString()}`;
}

/**
 * The same URL without the params this consumer owns — thread, ws, the
 * shortcut's open=chat and the inert source=pwa (other params, hash and path
 * intact). Foreign `open`/`source` values are left alone.
 */
export function stripThreadDeepLinkParams(href: string): string {
  try {
    const base = typeof window !== 'undefined' && window.location ? window.location.origin : 'https://prest0.ai';
    const url = new URL(href, base);
    url.searchParams.delete(THREAD_DEEP_LINK_PARAM);
    url.searchParams.delete(THREAD_DEEP_LINK_WS_PARAM);
    if (url.searchParams.get(OPEN_CHAT_PARAM) === OPEN_CHAT_VALUE) url.searchParams.delete(OPEN_CHAT_PARAM);
    if (url.searchParams.get(PWA_SOURCE_PARAM) === PWA_SOURCE_VALUE) url.searchParams.delete(PWA_SOURCE_PARAM);
    const search = url.searchParams.toString();
    return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  } catch {
    return href;
  }
}

/**
 * THE rule. `activeAccountId` is the resolved workspace (null while it is
 * still resolving). `canWait` — at boot the workspace resolves a moment after
 * the thread store; a link that names a workspace waits for it (never a
 * guess). Live requests never wait: an unresolved workspace opens nothing.
 */
export function decideThreadDeepLink(
  link: ThreadDeepLink | null,
  activeAccountId: string | null,
  opts: { canWait: boolean } = { canWait: false },
): ThreadDeepLinkDecision {
  if (!link) return { kind: 'none' };
  if (!link.ws) return { kind: 'open-here', threadId: link.threadId };
  const active = (activeAccountId || '').trim();
  if (!active) return opts.canWait ? { kind: 'wait' } : { kind: 'none' };
  if (active === link.ws) return { kind: 'open-here', threadId: link.threadId };
  return { kind: 'switch-workspace', threadId: link.threadId, ws: link.ws };
}

/**
 * MAY A BOOT LINK WAIT FOR ITS WORKSPACE? (2026-09-23)
 *
 * At boot, "may not wait" is final: the decision above is 'none', the params
 * are stripped and nothing is saved, so the thread the person tapped can never
 * open. That verdict is right only for a visitor, whom no workspace will ever
 * resolve for. Two states had been read as a visitor, and neither is one:
 *
 * - Sign-in still restoring. Until Firebase Auth restores a returning person,
 *   their uid is null, exactly like a visitor's (useGlobalChat's Point 6
 *   auth-init race). The chat engine mounts after the page paints, so on a
 *   slow phone the thread store is ready first. prod-e2e run 35842072769,
 *   attempt 2: the recipient's chat mounted at 57.7 s, sign-in began restoring
 *   at 59.16 s, and their link was thrown away in between.
 * - The "connecting to your workspace" hold (useGlobalChat's
 *   workspaceUnresolved): a signed-in person's workspace read failed and a
 *   retry is armed. The workspace is still coming, and nothing can open during
 *   the hold anyway.
 *
 * `identitySettling` is useGlobalChat's isMigrationGateChecking: true from
 * mount until the chat has decided who this is, sign-in restore included.
 */
export function bootThreadLinkCanWait(facts: { identitySettling: boolean; signedIn: boolean }): boolean {
  return facts.identitySettling || facts.signedIn;
}

export interface ThreadDeepLinkEffects {
  /** Activate a thread of the active workspace. Boot: savePendingThreadOpen; live: the chat's own open request. */
  openHere: (threadId: string) => void;
  /** The SEALED organization switch — the thread renders only after the reload, inside its own workspace. */
  switchWorkspace: (request: { targetAccountId: string; threadId: string }) => void;
  /** Remove the params from the address bar (default: history.replaceState). */
  stripUrl?: () => void;
  /** `open=chat`: open the chat surface (the host's existing open action; a phone gets the fullscreen takeover). */
  openChat?: () => void;
}

function defaultStripUrl(): void {
  try {
    if (typeof window === 'undefined' || !window.history) return;
    const next = stripThreadDeepLinkParams(window.location.href);
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== current) window.history.replaceState(window.history.state, '', next);
  } catch { /* history unavailable */ }
}

/**
 * Apply the decision for a link (boot consumer). Strips the params on every
 * outcome except 'wait' — a 'wait' keeps them for the re-run once the
 * workspace resolves.
 */
export function consumeThreadDeepLink(
  link: ThreadDeepLink | null,
  ctx: { activeAccountId: string | null; canWait: boolean },
  effects: ThreadDeepLinkEffects,
): ThreadDeepLinkDecision {
  const decision = decideThreadDeepLink(link, ctx.activeAccountId, { canWait: ctx.canWait });
  if (decision.kind === 'wait') return decision;
  if (link) (effects.stripUrl || defaultStripUrl)();
  if (decision.kind === 'open-here') effects.openHere(decision.threadId);
  else if (decision.kind === 'switch-workspace') {
    effects.switchWorkspace({ targetAccountId: decision.ws, threadId: decision.threadId });
  }
  return decision;
}

/**
 * Boot consumer over a query string (window.location by default) — the
 * default openHere is the pending-open marker. Also serves the manifest
 * shortcut: `open=chat` fires `effects.openChat` (after any thread decision,
 * never while one is waiting) and `source=pwa` is stripped with no behaviour.
 */
export function consumeAppUrlIntents(
  search: string,
  ctx: { activeAccountId: string | null; canWait: boolean },
  effects: Partial<ThreadDeepLinkEffects> & Pick<ThreadDeepLinkEffects, 'switchWorkspace'>,
): ThreadDeepLinkDecision {
  const link = parseThreadDeepLink(search);
  const strip = effects.stripUrl || defaultStripUrl;
  const decision = consumeThreadDeepLink(link, ctx, {
    openHere: effects.openHere || savePendingThreadOpen,
    switchWorkspace: effects.switchWorkspace,
    stripUrl: strip,
  });
  if (decision.kind === 'wait') return decision;
  const openChat = parseChatOpenIntent(search);
  // consumeThreadDeepLink already stripped when a thread link was present;
  // the shortcut / inert params strip here (idempotent either way).
  if (!link && hasAppUrlIntent(search)) strip();
  // A workspace switch hard-navigates — opening the pane underneath it would
  // paint a mixed-org frame for a beat. Everything else opens the chat.
  if (openChat && decision.kind !== 'switch-workspace') effects.openChat?.();
  return decision;
}

export function consumeThreadDeepLinkFromLocation(
  ctx: { activeAccountId: string | null; canWait: boolean },
  effects: Partial<ThreadDeepLinkEffects> & Pick<ThreadDeepLinkEffects, 'switchWorkspace'>,
): ThreadDeepLinkDecision {
  if (typeof window === 'undefined') return { kind: 'none' };
  return consumeAppUrlIntents(window.location.search, ctx, effects);
}

/**
 * A THREAD ASKED FOR AT BOOT IS SHOWN, NOT JUST SELECTED (2026-09-23).
 *
 * The boot consumer above turns a thread link (a notification tap with no
 * prest0 window open, or the sealed workspace switch's pending-open marker)
 * into a request to open that thread once the thread store is ready. It used
 * to do only half of that. It SELECTED the thread, and only if the store
 * already listed it at that instant: the chat stayed closed (a phone showed
 * the landing page and a chat bubble), and a thread that reached the list a
 * moment later (someone just added to it) was dropped for good. The live
 * path (a bell row, the service worker's OPEN_THREAD) always opened the pane,
 * through requestChatOpenForThread. The boot path now waits for the thread
 * and opens it through that same door.
 *
 * Containment is unchanged: the thread opens only once it is IN this
 * workspace's own list (never switchThread's minimal-record path, which has
 * no workspace to check against). The wait is bounded, and it stands down if
 * the person opens something else meanwhile, so a late arrival never takes
 * the view over from something they chose.
 *
 * "The person opens something else" is counted, never inferred from the
 * active thread changing (corrected the same day, from the first live run).
 * The store moves the active thread by itself during boot: on a fresh device
 * it drops its empty boot draft on the first snapshot and restores the
 * person's REMEMBERED thread. In prod-e2e run 35828940423 that happened in
 * the recipient's browser with nobody touching anything, the thread they had
 * just been added to was already listed, and the request was dropped as if
 * they had chosen the remembered thread. So the request records how many
 * navigations callers had asked the store for (useChatThreads'
 * getCallerNavigationCount: a person opening a thread, pressing +, archiving,
 * a bell row) and stands down only when that number moves.
 *
 * The wait is counted from when the SERVER's thread list arrives, never from
 * when the link was taken (corrected again the same day, from the second live
 * run). Until the first server snapshot, the list is only this device's cache,
 * and a thread missing from it proves nothing. In prod-e2e run 35842072769
 * attempt 2, the workspace resolved about 31 s after the page opened and the
 * server list arrived 38 s after that: a clock started when the link was taken
 * would have run out 8 s before the thread was listed. useChatThreads'
 * getServerListLoadedAtMs says when the list arrived.
 */
export const BOOT_THREAD_OPEN_WAIT_MS = 30_000;

export interface PendingBootThreadOpen {
  threadId: string;
  /** When the link was taken. */
  requestedAtMs: number;
  /**
   * How long the thread may take to reach the list, counted from when the server list arrived
   * (or from the request, when the list was already there). After that the request ends: a
   * thread that never arrives must not take the view over later.
   */
  waitMs: number;
  /**
   * How many navigations callers had asked the thread store for when the request was taken.
   * One more (the person opened something) cancels it. The store's own moves never count.
   */
  navigationsAtRequest: number;
}

export type BootThreadOpenStep =
  | { kind: 'wait' }
  | { kind: 'drop'; reason: 'expired' | 'moved-on' }
  | { kind: 'open'; threadId: string };

export function beginBootThreadOpen(
  threadId: string,
  nowMs: number,
  navigationCount: number,
  waitMs: number = BOOT_THREAD_OPEN_WAIT_MS,
): PendingBootThreadOpen {
  return { threadId, requestedAtMs: nowMs, waitMs, navigationsAtRequest: navigationCount };
}

/** One step of the boot open. Null when nothing is waiting. */
export function decideBootThreadOpen(
  pending: PendingBootThreadOpen | null,
  facts: {
    nowMs: number;
    listedIds: readonly string[];
    activeThreadId: string | null;
    /** The store's count of navigations callers have asked for (getCallerNavigationCount). */
    navigationCount: number;
    /** When the server's thread list first arrived this session (getServerListLoadedAtMs); null until then. */
    listLoadedAtMs: number | null;
  },
): BootThreadOpenStep | null {
  if (!pending) return null;
  // The deadline comes first: once it has passed, nothing opens, not even a
  // thread that later becomes active some other way. It has no clock until the
  // server list has arrived, because before that the thread's absence means
  // nothing.
  if (facts.listLoadedAtMs !== null) {
    const deadline = Math.max(pending.requestedAtMs, facts.listLoadedAtMs) + pending.waitMs;
    if (facts.nowMs > deadline) return { kind: 'drop', reason: 'expired' };
  }
  // Already the active thread (restored, or opened by the person): it only needs showing.
  if (facts.activeThreadId === pending.threadId) return { kind: 'open', threadId: pending.threadId };
  // The person asked for something else. A change of active thread the store
  // made by itself is not this, so it never cancels the request.
  if (facts.navigationCount !== pending.navigationsAtRequest) return { kind: 'drop', reason: 'moved-on' };
  if (facts.listedIds.includes(pending.threadId)) return { kind: 'open', threadId: pending.threadId };
  return { kind: 'wait' };
}

/**
 * LIVE: hand a thread URL to whatever prest0 window is listening. Returns
 * true when a visible chat claimed it (preventDefault handshake — the
 * openChatForFile idiom); false means "nobody home — navigate instead".
 */
export function requestOpenThreadUrl(url: string): boolean {
  if (typeof document === 'undefined') return false;
  const link = parseThreadDeepLinkFromUrl(url);
  if (!link) return false;
  const event = new CustomEvent<ThreadDeepLink>(OPEN_THREAD_URL_EVENT, { detail: link, cancelable: true });
  document.dispatchEvent(event);
  return event.defaultPrevented;
}

/**
 * Listen for live thread-URL requests: the document event above AND the
 * service worker's OPEN_THREAD message (a notification click that found this
 * window). The handler returns true when it took the request.
 */
export function subscribeOpenThreadUrl(handler: (link: ThreadDeepLink) => boolean): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const onDocument = (e: Event) => {
    const ce = e as CustomEvent<ThreadDeepLink>;
    const link = ce.detail;
    if (!link || !link.threadId) return;
    if (handler(link)) ce.preventDefault();
  };
  document.addEventListener(OPEN_THREAD_URL_EVENT, onDocument);
  const container = typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : null;
  const onSwMessage = (e: MessageEvent) => {
    const data = e.data as { type?: string; url?: string } | null;
    if (!data || data.type !== 'OPEN_THREAD') return;
    const link = parseThreadDeepLinkFromUrl(data.url);
    if (link) handler(link);
  };
  try { container?.addEventListener('message', onSwMessage); } catch { /* no container */ }
  return () => {
    document.removeEventListener(OPEN_THREAD_URL_EVENT, onDocument);
    try { container?.removeEventListener('message', onSwMessage); } catch { /* noop */ }
  };
}

/**
 * Open a thread from an in-app surface (a bell row): a visible chat takes it
 * live; otherwise navigate to the deep link and let the boot consumer decide
 * (same-workspace open or sealed switch) — never an in-place foreign open.
 */
export function openThreadFromNotification(link: { threadId: string; ws?: string | null }): void {
  const url = buildThreadDeepLinkUrl(link);
  if (requestOpenThreadUrl(url)) return;
  try {
    if (typeof window !== 'undefined') window.location.assign(url);
  } catch { /* navigation blocked */ }
}
